import { useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { motion, type Variants } from 'motion/react'
import { Badge, Button, Drawer, EmptyState, TextInput, DatePicker } from '../../../../shared/ui'
import {
  useCertifications, useCreateCertification, useDeleteCertification,
  type PersonCertificationRow, type CertStatus,
} from '../../api/hrPersonApi'
import { HrSection } from '../primitives/HrSection'
import type { HrPersonOutletContext } from '../HrPersonPage'

const DATE_FMT = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

const STATUS_LABEL: Record<CertStatus, string> = {
  missing:        'Fehlt',
  valid:          'Gültig',
  'expires-soon': 'Läuft bald ab',
  expired:        'Abgelaufen',
}

const STATUS_TONE: Record<CertStatus, 'neutral' | 'success' | 'warn' | 'danger'> = {
  missing:        'neutral',
  valid:          'success',
  'expires-soon': 'warn',
  expired:        'danger',
}

const STATUS_RING: Record<CertStatus, string> = {
  missing:        'ring-outline-variant/40 bg-surface-container-lowest/40',
  valid:          'ring-emerald-300/60 bg-emerald-50/40 dark:bg-emerald-500/5',
  'expires-soon': 'ring-amber-300/60 bg-amber-50/40 dark:bg-amber-500/5',
  expired:        'ring-rose-300/60 bg-rose-50/40 dark:bg-rose-500/5',
}

const STATUS_ICON_TINT: Record<CertStatus, string> = {
  missing:        'text-on-surface-variant/50',
  valid:          'text-emerald-600 dark:text-emerald-400',
  'expires-soon': 'text-amber-600 dark:text-amber-400',
  expired:        'text-rose-600 dark:text-rose-400',
}

const gridVariants: Variants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.05, delayChildren: 0.04 } },
}
const stampVariants: Variants = {
  hidden: { opacity: 0, scale: 0.85, rotate: -4 },
  show:   { opacity: 1, scale: 1, rotate: 0, transition: { type: 'spring', stiffness: 280, damping: 18 } },
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : DATE_FMT.format(d)
}

function todayPlusMonths(months: number | null): string {
  if (!months) return ''
  const d = new Date()
  d.setMonth(d.getMonth() + months)
  return d.toISOString().slice(0, 10)
}

export default function ComplianceTab() {
  const { mainId } = useOutletContext<HrPersonOutletContext>()
  const { data, isLoading, isError, error } = useCertifications(mainId)
  const createMut = useCreateCertification(mainId)
  const deleteMut = useDeleteCertification(mainId)

  const [editing, setEditing] = useState<PersonCertificationRow | null>(null)

  const stats = useMemo(() => {
    const rows = data?.certifications ?? []
    return {
      total:    rows.length,
      valid:    rows.filter(r => r.status === 'valid').length,
      expiring: rows.filter(r => r.status === 'expires-soon').length,
      expired:  rows.filter(r => r.status === 'expired').length,
      missing:  rows.filter(r => r.status === 'missing').length,
    }
  }, [data])

  if (isLoading) {
    return (
      <HrSection title="Compliance" subtitle="Schulungen + Belehrungen + Vorsorge">
        <ComplianceSkeleton />
      </HrSection>
    )
  }

  if (isError) {
    return (
      <HrSection title="Compliance" subtitle="Schulungen + Belehrungen + Vorsorge">
        <EmptyState
          icon={(error as { status?: number }).status === 403 ? 'lock' : 'error'}
          title={(error as { status?: number }).status === 403 ? 'Kein Zugriff' : 'Konnte nicht geladen werden'}
          description={error instanceof Error ? error.message : 'Unbekannter Fehler'}
        />
      </HrSection>
    )
  }

  const rows = data?.certifications ?? []

  return (
    <HrSection
      title="Compliance"
      subtitle="Schulungen + Belehrungen + Vorsorge · Stempel pro Zertifikatstyp"
      actions={
        <div className="flex items-center gap-2">
          {stats.expired > 0 ? <Badge tone="danger">{stats.expired} abgelaufen</Badge> : null}
          {stats.expiring > 0 ? <Badge tone="warn">{stats.expiring} läuft ab</Badge> : null}
          <Badge tone={stats.missing === 0 && stats.expired === 0 ? 'success' : 'neutral'}>
            {stats.valid + stats.expiring}/{stats.total} gültig
          </Badge>
        </div>
      }
    >
      {rows.length === 0 ? (
        <EmptyState
          icon="badge"
          title="Keine Zertifikatstypen konfiguriert"
          description="In ops.CertificationType ist noch nichts gepflegt. Migration 131 seedet 8 Standard-Typen — wurde sie ausgeführt?"
        />
      ) : (
        <motion.div
          variants={gridVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {rows.map(row => (
            <motion.button
              key={row.certTypeId}
              variants={stampVariants}
              type="button"
              onClick={() => setEditing(row)}
              className={`text-left rounded-2xl ring-1 p-4 flex flex-col gap-2 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${STATUS_RING[row.status]}`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`material-symbols-outlined text-3xl flex-shrink-0 ${STATUS_ICON_TINT[row.status]}`}
                  aria-hidden="true"
                >
                  {row.icon || 'verified'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-on-surface leading-tight">{row.label}</div>
                  <Badge tone={STATUS_TONE[row.status]} size="sm">{STATUS_LABEL[row.status]}</Badge>
                </div>
              </div>
              <div className="text-xs text-on-surface-variant mt-1 space-y-0.5">
                {row.issuedAt ? (
                  <div>Ausgestellt: <span className="font-medium text-on-surface">{fmtDate(row.issuedAt)}</span></div>
                ) : null}
                {row.expiresAt ? (
                  <div>Gültig bis: <span className="font-medium text-on-surface">{fmtDate(row.expiresAt)}</span></div>
                ) : null}
                {row.issuingAuthority ? (
                  <div className="truncate">von <span className="font-medium text-on-surface">{row.issuingAuthority}</span></div>
                ) : null}
                {!row.issuedAt ? (
                  <div className="italic">— noch nicht erfasst — klicken zum Eintragen</div>
                ) : null}
              </div>
            </motion.button>
          ))}
        </motion.div>
      )}

      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.label ?? ''}
      >
        {editing ? (
          <CertificationForm
            row={editing}
            onSave={(body) => createMut.mutateAsync(body).then(() => setEditing(null))}
            onDelete={
              editing.certificationId
                ? () => deleteMut.mutateAsync(editing.certificationId!).then(() => setEditing(null))
                : null
            }
            saving={createMut.isPending}
            deleting={deleteMut.isPending}
          />
        ) : null}
      </Drawer>
    </HrSection>
  )
}

interface FormProps {
  row: PersonCertificationRow
  onSave: (body: { certTypeId: number; issuedAt: string; expiresAt: string | null; issuingAuthority: string | null; notes: string | null }) => Promise<unknown>
  onDelete: (() => Promise<unknown>) | null
  saving: boolean
  deleting: boolean
}

function CertificationForm({ row, onSave, onDelete, saving, deleting }: FormProps) {
  const todayIso = new Date().toISOString().slice(0, 10)
  const [issuedAt, setIssuedAt] = useState(row.issuedAt ?? todayIso)
  const [expiresAt, setExpiresAt] = useState(
    row.expiresAt ?? (row.defaultExpiryMonths ? todayPlusMonths(row.defaultExpiryMonths) : ''),
  )
  const [issuingAuthority, setIssuingAuthority] = useState(row.issuingAuthority ?? '')
  const [notes, setNotes] = useState(row.notes ?? '')

  function handleSubmit() {
    if (!issuedAt) return
    onSave({
      certTypeId: row.certTypeId,
      issuedAt,
      expiresAt: expiresAt || null,
      issuingAuthority: issuingAuthority.trim() || null,
      notes: notes.trim() || null,
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant block mb-1">
          Ausgestellt am
        </label>
        <DatePicker value={issuedAt} onChange={setIssuedAt} />
      </div>

      <div>
        <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant block mb-1">
          Gültig bis {row.defaultExpiryMonths ? `(Standard: ${row.defaultExpiryMonths} Monate)` : '(unbefristet wenn leer)'}
        </label>
        <DatePicker value={expiresAt} onChange={setExpiresAt} />
      </div>

      {row.requiresIssuer ? (
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant block mb-1">
            Ausstellende Stelle *
          </label>
          <TextInput
            value={issuingAuthority}
            onChange={e => setIssuingAuthority(e.target.value)}
            placeholder="z.B. Gesundheitsamt Hamburg"
          />
        </div>
      ) : (
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant block mb-1">
            Ausstellende Stelle (optional)
          </label>
          <TextInput
            value={issuingAuthority}
            onChange={e => setIssuingAuthority(e.target.value)}
            placeholder="z.B. Internes Training"
          />
        </div>
      )}

      <div>
        <label className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant block mb-1">
          Notizen
        </label>
        <TextInput
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="optional"
        />
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-outline-variant/30">
        {onDelete ? (
          <Button variant="ghost" onClick={onDelete} disabled={deleting || saving}>
            <span className="material-symbols-outlined text-base mr-1" aria-hidden="true">archive</span>
            {deleting ? 'Archiviert…' : 'Archivieren'}
          </Button>
        ) : <span />}
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!issuedAt || saving || (row.requiresIssuer && !issuingAuthority.trim())}
        >
          {saving ? 'Speichert…' : row.certificationId ? 'Aktualisieren' : 'Speichern'}
        </Button>
      </div>
    </div>
  )
}

function ComplianceSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-28 rounded-2xl bg-slate-200" />
      ))}
    </div>
  )
}
